import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Combobox,
  Group,
  Stack,
  Text,
  TextInput,
  Tooltip,
  useCombobox,
} from '@mantine/core';
import { X } from 'react-feather';
import {
  pairKey,
  searchParticipants,
  unrecognizedDoNotMatch,
} from '../../model/derive';
import { useSetup } from '../../SetupContext';
import type { Participant, ParticipantId } from '../../model/types';
import classes from './DataStep.module.css';

const MAX_RESULTS = 5;

interface DnmCellProps {
  participant: Participant;
  /** Everyone this person is kept apart from, in both directions. */
  partners: ParticipantId[];
  /** Pair keys from the session data, to tell an override from a source pair. */
  sourcePairs: Set<string>;
}

/**
 * The DO NOT MATCH column: removable chips plus a search-to-add box.
 * Pairs are reflexive, so adding or removing someone here changes their row
 * too. Emails from SGOS that match no participant show with a "Not
 * recognized" badge; they can be removed but not added.
 */
export function DnmCell({ participant, partners, sourcePairs }: DnmCellProps) {
  const { session, config, update, readOnly } = useSetup();
  const [query, setQuery] = useState('');
  const combobox = useCombobox();
  const byId = new Map(session.participants.map(p => [p.id, p]));
  const unrecognized = unrecognizedDoNotMatch(participant, config);
  const results = query.trim()
    ? searchParticipants(session.participants, query)
        .filter(p => p.id !== participant.id && !partners.includes(p.id))
        .slice(0, MAX_RESULTS)
    : [];

  const change = (
    type: 'addDoNotMatch' | 'removeDoNotMatch',
    otherId: ParticipantId
  ) =>
    update({
      type,
      a: participant.id,
      b: otherId,
      inSource: sourcePairs.has(pairKey(participant.id, otherId)),
    });

  const add = (otherId: string) => {
    change('addDoNotMatch', otherId);
    setQuery('');
    combobox.closeDropdown();
  };

  return (
    <Stack gap={6} miw={0}>
      {partners.length === 0 && unrecognized.length === 0 && (
        <Text size="sm" c="dimmed">
          No one
        </Text>
      )}
      {partners.map(id => {
        const other = byId.get(id);
        const label = other?.email || other?.name || id;
        return (
          <Group
            key={id}
            className={classes.chip}
            gap={8}
            wrap="nowrap"
            justify="space-between"
          >
            <Text size="sm" className={classes.wrap}>
              {label}
            </Text>
            {!readOnly && (
              <ActionIcon
                size={22}
                radius="xl"
                variant="light"
                color="gray"
                aria-label={`Remove ${label}`}
                onClick={() => change('removeDoNotMatch', id)}
              >
                <X size={10} />
              </ActionIcon>
            )}
          </Group>
        );
      })}
      {unrecognized.map(email => (
        <Group
          key={email}
          className={classes.chip}
          data-unrecognized
          gap={8}
          wrap="nowrap"
          justify="space-between"
        >
          <Stack gap={2} miw={0}>
            <Text size="sm" className={classes.wrap}>
              {email}
            </Text>
            <Tooltip
              label="No participant in this cohort has this email, so matching will skip it."
              multiline
              w={240}
              withArrow
            >
              <Badge
                color="dangerRed"
                variant="light"
                size="xs"
                w="fit-content"
              >
                Not recognized
              </Badge>
            </Tooltip>
          </Stack>
          {!readOnly && (
            <ActionIcon
              size={22}
              radius="xl"
              variant="light"
              color="gray"
              aria-label={`Remove ${email}`}
              onClick={() =>
                update({
                  type: 'removeUnrecognizedDoNotMatch',
                  id: participant.id,
                  email,
                })
              }
            >
              <X size={10} />
            </ActionIcon>
          )}
        </Group>
      ))}
      {!readOnly && (
        <Combobox store={combobox} onOptionSubmit={add}>
          <Combobox.Target>
            <TextInput
              size="xs"
              placeholder="Search participants to add"
              aria-label={`Add someone ${participant.name} should not be matched with`}
              value={query}
              onChange={e => {
                setQuery(e.currentTarget.value);
                combobox.openDropdown();
              }}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => combobox.closeDropdown()}
            />
          </Combobox.Target>
          {query.trim() !== '' && (
            <Combobox.Dropdown>
              <Combobox.Options>
                {results.length === 0 ? (
                  <Combobox.Empty>No participants found.</Combobox.Empty>
                ) : (
                  results.map(p => (
                    <Combobox.Option key={p.id} value={p.id}>
                      <Text size="sm" fw={500}>
                        {p.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {p.email}
                      </Text>
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Combobox.Dropdown>
          )}
        </Combobox>
      )}
    </Stack>
  );
}
